import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef } from '@angular/core';
import { ImageModule } from '@df/module-image/image.module';
import { ButtonsOptions, ColorOptions, HorizontalAlignmentOptions, TextStyleOptions } from '@df/ui/atomic/atomic-options';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { DfUiModule } from '@df/ui/df-ui.module';
import { VimeoPlayerComponent } from '@df/ui/display/media/vimeo-player.component';
import { YoutubePlayerComponent } from '@df/ui/display/media/youtube-player.component';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { NTextOverlay, TextOverlayComponent } from 'projects/app-default/src/app/common/text-overlay/text-overlay.component';
import { PROMOTION_FIELDS, getCmsContentFieldConfig } from '../../../../../cms/components/components.config';
import {
    ICmsComponentContentData,
    ICmsContentVariant,
    ICmsContentVideoProvider,
    ICmsPromotionData
} from '../../../../../cms/components/components.interface';
import { ShellComponent } from '@df/ui/common/shell/shell.component';
export interface IPageComponentHeroBannerData
    extends NICmsContentComponent.IData,
        ICmsPromotionData,
        ICmsComponentContentData,
        NTextOverlay.IData {
    youtube_id?: string;
    text_alignment?: string;
    image?: string;
    ctas: CTAData[];
    title_color?: string;
    title_top: string;
    title_bottom: string;
    shadow?: boolean;
}
export interface CTAData {
    link: string;
    text: string;
    cta_type?: 'button_type' | 'link_type';
    button?: string;
    link_font_style?: string;
    link_color?: string;
    shadow?: boolean;
}
@ICmsComponent<PageComponentHeroBannerComponent>({
    id: 'hero-banner',
    name: 'Hero Banner',
    tags: ['hero', 'image', 'banner'],
    fields: [
        ...PROMOTION_FIELDS,
        {
            type: NICmsContentField.EFieldType.label,
            key: 'section-image',
            title: 'Image'
        },
        ...getCmsContentFieldConfig('image'),
        ...NTextOverlay.getFields(true),
        {
            type: NICmsContentField.EFieldType.label,
            key: 'section-text',
            title: 'Text',
            canShow: (data: NTextOverlay.IData) => !data?.overlayText
        },
        <NICmsContentField.IChoiceField>{
            type: NICmsContentField.EFieldType.choice,
            key: 'text_alignment',
            title: 'Text Alignment',
            default: 'left',
            options: HorizontalAlignmentOptions,
            canShow: (data: NTextOverlay.IData) => !data?.overlayText
        },
        {
            type: NICmsContentField.EFieldType.label,
            key: 'section-image',
            title: 'Title',
            canShow: (data: NTextOverlay.IData) => !data?.overlayText
        },
        <NICmsContentField.IField>{
            type: NICmsContentField.EFieldType.text,
            key: 'title_top',
            title: 'Title top',
            required: true,
            default: 'Heading',
            canShow: (data: NTextOverlay.IData) => !data?.overlayText
        },
        <NICmsContentField.IField>{
            type: NICmsContentField.EFieldType.text,
            key: 'title_bottom',
            title: 'Title bottom',
            required: true,
            default: 'Second title',
            canShow: (data: NTextOverlay.IData) => !data?.overlayText
        },
        <NICmsContentField.IChoiceField>{
            key: 'title_color',
            type: NICmsContentField.EFieldType.choice,
            title: 'Colour',
            previewMode: 'color',
            options: ColorOptions,
            default: 'col-b',
            canShow: (data: NTextOverlay.IData) => !data?.overlayText
        },
        {
            key: 'shadow',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Add shadow',
            description: 'Add shadow under the text',
            default: true,
            canShow: (data: NTextOverlay.IData) => !data?.overlayText
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'ctas',
            title: 'CTA',
            itemName: 'CTA',
            required: true,
            canShow: (data: NTextOverlay.IData) => !data?.overlayText,
            fields: [
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'text',
                    title: 'Text',
                    required: true
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'link',
                    title: 'Link',
                    required: true
                },
                <NICmsContentField.IChoiceField>{
                    key: 'cta_type',
                    title: 'Cta type',
                    type: NICmsContentField.EFieldType.choice,
                    options: [
                        { key: 'button_type', title: 'Button' },
                        { key: 'link_type', title: 'Link' }
                    ],
                    default: 'button_type'
                },
                <NICmsContentField.IChoiceField>{
                    key: 'button',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Button type',
                    options: ButtonsOptions,
                    default: 'button-inline-1',
                    canShow: data => data.cta_type === 'button_type'
                },
                <NICmsContentField.IChoiceField>{
                    key: 'link_font_style',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Link Font Style',
                    options: TextStyleOptions,
                    default: 'p1',
                    canShow: data => data.cta_type === 'link_type'
                },
                <NICmsContentField.IChoiceField>{
                    key: 'link_color',
                    type: NICmsContentField.EFieldType.choice,
                    title: 'Link copy colour',
                    previewMode: 'color',
                    options: ColorOptions,
                    default: 'col-1',
                    canShow: data => data.cta_type === 'link_type'
                },
                {
                    key: 'shadow',
                    type: NICmsContentField.EFieldType.toggle,
                    title: 'Add shadow',
                    description: 'Add shadow under the text'
                }
            ],
            default: [{ text: 'Button', link: '/', button: 'button-inline-1' }]
        }
    ]
})
@Component({
    selector: 'page-component-hero-banner',
    templateUrl: 'hero-banner.html',
    styleUrls: ['./hero-banner.scss'],
    standalone: true,
    imports: [
        DfUiModule,
        HrefComponent,
        ICmsClientModule,
        ImageModule,
        NgClass,
        ShellComponent,
        SizeClassDirective,
        TextOverlayComponent,
        VimeoPlayerComponent,
        YoutubePlayerComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PageComponentHeroBannerComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'component'
    })
    component!: IPageComponentHeroBannerData;

    constructor(private elementRef: ElementRef) {
        super();
    }

    contentVariant = ICmsContentVariant;
    contentVideoProvider = ICmsContentVideoProvider;
    vimeoHeight?: number;
    vimeoWidth?: number;
    onPlayerInit(vimeoPlayerComponent: VimeoPlayerComponent): void {
        Promise.all([vimeoPlayerComponent.getVideoHeight(), vimeoPlayerComponent.getVideoWidth()]).then((result: number[]) => {
            this.vimeoHeight = result[0];
            this.vimeoWidth = result[1];
            if (this.vimeoHeight && this.vimeoWidth) {
                this.elementRef.nativeElement.style.setProperty('--vimeo-height', `${this.vimeoHeight / (this.vimeoWidth / 100)}vw`);
                this.elementRef.nativeElement.style.setProperty('--vimeo-width', `${this.vimeoWidth / (this.vimeoHeight / 100)}vh`);
            }
            this.detectChanges();
        });
    }

    protected buttonShadow(buttonClass: string | undefined): string {
        if (buttonClass && buttonClass.match('button-inline')) {
            return '_underline-shadow _text-shadow';
        } else {
            return '_button-box-shadow';
        }
    }
}
